import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

// #design-pass: Button-Varianten auf neue Tokens. Akzent-Primary, weiche danger,
// explizite hover-Hintergründe (kein opacity-Trick). radius-sm (6px).
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-semibold shadow-xs transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-accent text-white hover:bg-accent-hover",
        destructive: "border border-danger-border bg-danger-bg text-danger hover:bg-danger hover:text-white",
        outline: "border border-border-strong bg-surface text-text shadow-none hover:bg-surface-hover",
        secondary: "bg-surface2 text-text shadow-none hover:bg-surface3",
        ghost: "text-text2 shadow-none hover:bg-surface2 hover:text-text",
        link: "text-accent shadow-none underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-3.5",
        sm: "h-[30px] px-2.5 text-[12.5px]",
        lg: "h-[42px] px-[18px] text-[14.5px]",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
